import { ArrowLeftCircleIcon, ArrowRightCircleIcon, UsersIcon } from '@heroicons/react/24/outline'
import { GlobalConfigType, setLoanData } from 'actions'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { updateLoanFields } from 'services'
import { getOpenLoanDisplayNo } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { getSimliarIneligibleParties, IneligiblePartiesList, isIneligiblePartyCleared } from './logic'
import { floaterPanelEdgeClass, floaterTabEdgeClass, FloaterTabWrapper, useFloaterTab } from './useFloaterTab'

export const IneligibleParties = () => {
  const { loanDetail, loan, borrower, ineligibleParties } = useSelector((state: any) => {
    return {
      loanDetail: state.loanDetail,
      borrower: state.borrower,
      loan: state.loan,
      ineligibleParties: state.globalConfigReducer[GlobalConfigType.IneligibleParties],
    }
  })
  const [loading, setLoading] = useState(false)
  const [isShowSlide, setShowSlide] = useState(false)
  const floater = useFloaterTab('ineligibleParty', { side: 'left', percent: 20, offset: 120, mobileTop: 360 })

  const dispatch = useDispatch()

  const onShowSlide = () => {
    setShowSlide(!isShowSlide)
  }

  const onClearIneligibleParty = async (key: string, value: any) => {
    const newValue = cloneDeep(loan.ineligiblePartyCleared)
    newValue[key] = value
    dispatch(setLoanData({ key: 'ineligiblePartyCleared', data: newValue }))
    setLoading(true)
    await updateLoanFields({ ineligiblePartyCleared: newValue }, false)
    setLoading(false)
  }

  const similarParties = useMemo(() => {
    return getSimliarIneligibleParties()
  }, [loanDetail?.closeParties, ineligibleParties])

  const hasSimliarParty = useMemo(() => {
    let rtl = false
    Object.keys(similarParties).map((key) => {
      if (similarParties[key].length) rtl = true
    })
    return rtl
  }, [similarParties])

  const color = useMemo(() => {
    return isIneligiblePartyCleared() ? 'shade-blue' : 'red-500'
  }, [loan, loanDetail?.closeParties, borrower, ineligibleParties])

  if (!hasSimliarParty) return <></>

  return (
    <>
      <LayoutLoading show={loading} />
      <FloaterTabWrapper floater={floater}>
        <button
          className={`p-3 text-sm ${floaterTabEdgeClass(
            floater.side,
          )} ring-[1px] ring-${color} bg-${color} border-${color} hover:text-${color} text-white border hover:bg-white rounded flex items-center gap-2 w-auto sm:w-[155px]`}
          {...floater.tabProps(onShowSlide)}
        >
          <UsersIcon className="w-6 h-6" />
          <span className="hidden sm:inline">Potential Ineligible Party</span>
        </button>
      </FloaterTabWrapper>
      {isShowSlide && (
        <div
          className={`z-50 shadow1 fixed bg-white border top-[5vh] bottom-0 w-[24rem] max-w-full h-[90vh] overflow-auto ease-in-out ${floaterPanelEdgeClass(
            floater.side,
          )}`}
        >
          <div className="w-[24rem] max-w-full fixed z-20 rounded-t flex justify-between p-3 font-bold text-sm place-items-center items-center text-shade-blue border-b bg-white">
            <div className="flex items-center">
              <p className="text-lg text-gray-800">Potential Ineligible Party: </p>
              <span className="text-[16px] ml-2 italic font-semibold">
                {getOpenLoanDisplayNo(loanDetail)}
              </span>
            </div>

            <button className="hover:underline hover-shadow1 p-1 rounded" onClick={onShowSlide}>
              {floater.side === 'left' ? (
                <ArrowLeftCircleIcon className="w-5 h-5" />
              ) : (
                <ArrowRightCircleIcon className="w-5 h-5" />
              )}
            </button>
          </div>
          <div className="pt-14 h-fit overflow-auto relative text-sm text-gray-900">
            {Object.keys(IneligiblePartiesList).map((key) => {
              if (!similarParties[key].length) return <></>
              return (
                <>
                  <p className="bg-blue-100 text-shade-blue font-bold p-3 flex justify-between">
                    <span>Potential Ineligible {IneligiblePartiesList[key]}</span>
                  </p>
                  <table className="w-full">
                    <tbody>
                      {similarParties[key].map((item, index: number) => (
                        <tr className="border-b" key={index}>
                          <td className="px-3 py-2 flex justify-between items-center">
                            <span>
                              {item[0]} : {item[1]}
                            </span>
                            <RenderInput
                              input={{
                                inputType: 'toggle',
                                title: 'Clear',
                                value: loan.ineligiblePartyCleared?.[`${item[0]}-${item[1]}`],
                              }}
                              Key={`${item[0]}-${item[1]}`}
                              onChange={onClearIneligibleParty}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )
            })}
          </div>
        </div>
      )}
    </>
  )
}
